import { Link } from "react-router-dom";
import { ShieldCheck, Cctv, Dog, Network, ArrowUpRight } from "lucide-react";

const ICONS = { ShieldCheck, Cctv, Dog, Network };

const services = [
  {
    id: "manned-guarding",
    name: "Manned Guarding",
    short: "On-Site Deterrence & Rapid Response",
    desc: "SIA-licensed officers preventing incidents before they happen — across substations, construction sites, industrial units and events.",
    icon: "ShieldCheck",
    span: "lg:col-span-7 lg:row-span-2",
    image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1400&q=80",
    accent: "from-cyan-500/30",
  },
  {
    id: "cctv",
    name: "CCTV & Visual Surveillance",
    short: "AI-Augmented Eyes",
    desc: "Rapid-deploy towers, pods and fixed installations — feeding our UK control room 24/7.",
    icon: "Cctv",
    span: "lg:col-span-5",
    image: "https://images.unsplash.com/photo-1696133937088-7925539ed9a2?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    accent: "from-blue-500/30",
  },
  {
    id: "k9-patrols",
    name: "K9 Patrols",
    short: "Trained Detection. Quiet Power.",
    desc: "Certified handlers and trained dogs — the fastest, quietest deterrent on high-risk sites.",
    icon: "Dog",
    span: "lg:col-span-5",
    image: "https://images.unsplash.com/photo-1568572933382-74d440642117?auto=format&fit=crop&w=1200&q=80",
    accent: "from-emerald-500/30",
  },
  {
    id: "security-systems",
    name: "Security Systems",
    short: "Integrated Tech Stack",
    desc: "Access control, intruder alarms and remote monitoring — unified into a single operational picture.",
    icon: "Network",
    span: "lg:col-span-7",
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1400&q=80",
    accent: "from-cyan-500/30",
  },
];

export const ServicesBento = () => {
  return (
    <section data-testid="services-section" id="services" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-6 mb-12 items-end">
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">
              ◆ Capabilities
            </p>
            <h2 data-testid="services-headline" className="font-display font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tighter">
              Four pillars of protection,<br />
              <span className="text-gradient-cyan">engineered as one system.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-slate-400 leading-relaxed">
              From the officer on the ground to the operator behind the monitor — every layer is briefed, trained and equipped to work together. The result: fewer incidents, faster response, sharper outcomes.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 lg:grid-rows-2 gap-5">
          {services.map((s, idx) => {
            const Icon = ICONS[s.icon];
            return (
              <Link
                to="/services"
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className={`group relative card-tech rounded-xl overflow-hidden corner-brackets ${s.span} min-h-[260px] flex flex-col`}
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <div className="absolute inset-0 z-0">
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover opacity-30 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700" loading="lazy" />
                  <div className={`absolute inset-0 bg-gradient-to-tr ${s.accent} via-transparent to-transparent opacity-60`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/70 to-transparent" />
                </div>

                <div className="relative z-10 p-7 lg:p-8 flex flex-col h-full">
                  <div className="flex items-start justify-between mb-auto">
                    <div className="w-12 h-12 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-cyan-500/10 group-hover:border-cyan-400/30 transition">
                      <Icon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-slate-500 group-hover:text-cyan-400 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition" />
                  </div>

                  <div className="mt-12">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 mb-2">
                      0{idx + 1} · {s.short}
                    </p>
                    <h3 className="font-display font-bold text-white text-2xl lg:text-3xl tracking-tight mb-2">
                      {s.name}
                    </h3>
                    <p className="text-sm text-slate-400 leading-relaxed max-w-md">{s.desc}</p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};
